import { useEffect, useState } from 'react'
import {
  typedCollection,
  getCollection,
  orderBy,
  where,
} from '@/lib/firebase/firestore'
import type { CompanyMemberDocument } from '@/types'

/**
 * Loads active company members once and returns `[{ id, name }]` for assignee pickers.
 * Sorted by name ascending. Silently swallows errors for v1 — caller will simply see an
 * empty list (e.g. if a composite index is missing on `isActive ASC + name ASC`).
 */
export function useCompanyMembers() {
  const [members, setMembers] = useState<Array<{ id: string; name: string }>>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getCollection(
      typedCollection<CompanyMemberDocument>('company'),
      where('isActive', '==', true),
      orderBy('name', 'asc')
    )
      .then((docs) => {
        if (cancelled) return
        setMembers(docs.map((d) => ({ id: d.id, name: d.name })))
      })
      .catch((err) => {
        console.error('[useCompanyMembers] failed to load company members', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return { members, loading }
}

export default useCompanyMembers
